/**
 * Light / Dark / System theme store. See [[theming]].
 *
 * The preference lives on the device (`localStorage`), and the resolved theme is
 * applied as a `.dark` class on `<html>` — the hook Tailwind's `dark:` variant
 * and the `.dark` token block in index.css both key off.
 *
 * `index.html` carries a small inline script that does the same resolve-and-apply
 * before first paint so a dark user never sees a light flash. Keep the storage
 * key, the accepted values and the dark theme-color in step with it.
 */

export type ThemePreference = "system" | "light" | "dark";
export type ResolvedTheme = "light" | "dark";

export const THEME_STORAGE_KEY = "otus:theme";

/** What the `<meta name="theme-color">` tint becomes per resolved theme. Light is
 * the brand default already in index.html; dark matches the dark page surface. */
const THEME_COLOR: Record<ResolvedTheme, string> = {
  light: "#171717",
  dark: "#0A0A0B",
};

const DARK_QUERY = "(prefers-color-scheme: dark)";

function isPreference(value: unknown): value is ThemePreference {
  return value === "system" || value === "light" || value === "dark";
}

/** Anything unrecognised (null, a stale value, garbage) means "System". */
function parsePreference(value: string | null): ThemePreference {
  return isPreference(value) ? value : "system";
}

function darkQuery(): MediaQueryList | null {
  return typeof window !== "undefined" && typeof window.matchMedia === "function"
    ? window.matchMedia(DARK_QUERY)
    : null;
}

export function resolveTheme(
  preference: ThemePreference,
  systemPrefersDark: boolean,
): ResolvedTheme {
  if (preference === "system") return systemPrefersDark ? "dark" : "light";
  return preference;
}

function readStoredPreference(): ThemePreference {
  try {
    return parsePreference(localStorage.getItem(THEME_STORAGE_KEY));
  } catch {
    return "system";
  }
}

function writeStoredPreference(preference: ThemePreference): void {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    // Private-mode / blocked site data: the choice still holds for this session
    // (it lives in `preference` below) and just won't survive the reload.
  }
}

// In-memory copy so the choice holds for the session even when storage is
// blocked. Read lazily so importing this module never touches `localStorage`.
let preference: ThemePreference | null = null;
const listeners = new Set<() => void>();

export function getThemePreference(): ThemePreference {
  preference ??= readStoredPreference();
  return preference;
}

function applyTheme(): void {
  if (typeof document === "undefined") return;
  const resolved = resolveTheme(
    getThemePreference(),
    darkQuery()?.matches ?? false,
  );
  document.documentElement.classList.toggle("dark", resolved === "dark");
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute("content", THEME_COLOR[resolved]);
}

function notify(): void {
  listeners.forEach((listener) => listener());
}

export function setThemePreference(next: ThemePreference): void {
  preference = next;
  writeStoredPreference(next);
  applyTheme();
  notify();
}

/** `useSyncExternalStore` subscription. */
export function subscribeToTheme(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

let initialised = false;

/**
 * Applies the stored theme and keeps it live: follows the device setting while
 * the preference is System, and mirrors a change made in another tab. Idempotent.
 * Call once at startup, before the first render.
 */
export function initTheme(): void {
  if (initialised || typeof window === "undefined") return;
  initialised = true;
  applyTheme();

  darkQuery()?.addEventListener("change", () => {
    if (getThemePreference() === "system") applyTheme();
  });

  window.addEventListener("storage", (event) => {
    // `key === null` is `localStorage.clear()` in another tab.
    if (event.key !== null && event.key !== THEME_STORAGE_KEY) return;
    preference = parsePreference(event.newValue);
    applyTheme();
    notify();
  });
}
